import React, { useEffect, useState } from 'react';
import { IPublicClientApplication } from '@azure/msal-browser';
import { createTask, createChecklistItem, getProjectCollections } from '../services/taskService';
import { Collection, TaskPriority, TaskStatus, TaskType, Sprint } from '../types';
import { getProjectSprints, moveTaskToSprint, SprintStatus } from '../services/sprintService';
import PeoplePicker, { Person } from '../components/PeoplePicker';
import TagSection from '../components/TagSection';
import { Tag, TASK_TAG_POOL, attachTags } from '../services/tagService';
import { BM_TITLE_INPUT } from '../components/BoardModal';
import { Task } from '../types';
import { RecurrenceInput, createRecurrence } from '../services/recurrenceService';
import RecurrenceControls, { DEFAULT_RECURRENCE } from '../components/RecurrenceControls';

interface Props {
  pca: IPublicClientApplication;
  projectId: string;
  collectionId?: string;
  initialSortOrder?: number;
  initialTitle?: string;
  initialDescription?: string;
  /** True for Agile projects — surfaces the Sprint assignment field and
   *  makes Story a first-class work-item type in the Type control. */
  agile?: boolean;
  /** Default work-item type (e.g. Story when created from the Backlog). */
  initialType?: number;
  /** Pre-select a sprint (e.g. the sprint currently open in Sprint view). */
  initialSprintId?: string;
  onCreated: (taskId: string) => void;
  onCancel: () => void;
}

type PriorityChoice = 'none' | 'critical' | 'high' | 'medium' | 'low';
const PRIORITY_VALUE: Record<PriorityChoice, number | null> = {
  none:     null,
  critical: TaskPriority.Critical,
  high:     TaskPriority.High,
  medium:   TaskPriority.Medium,
  low:      TaskPriority.Low,
};

const STATUS_OPTIONS: { value: number; label: string }[] = [
  { value: TaskStatus.NotStarted, label: 'Not Started' },
  { value: TaskStatus.InProgress, label: 'In Progress' },
  { value: TaskStatus.InReview,   label: 'In Review' },
  { value: TaskStatus.Done,       label: 'Done' },
  { value: TaskStatus.OnHold,     label: 'On Hold' },
  { value: TaskStatus.Cancelled,  label: 'Cancelled' },
];

const sectionLabel: React.CSSProperties = {
  fontSize: 11, fontWeight: 600, letterSpacing: '0.22em',
  textTransform: 'uppercase', color: 'var(--ink-4)',
  fontFamily: 'var(--font-ui)',
  marginTop: 20, marginBottom: 10,
};
const firstSectionLabel: React.CSSProperties = { ...sectionLabel, marginTop: 0 };

const fieldLabel: React.CSSProperties = {
  display: 'block',
  fontSize: 12, color: 'var(--ink-3)', marginBottom: 4,
  fontFamily: 'var(--font-ui)',
};

interface DraftChecklistItem { tempId: string; text: string }
let nextTempId = 1;

export default function NewTaskModal({ pca, projectId, collectionId, initialSortOrder, initialTitle, initialDescription, agile, initialType, initialSprintId, onCreated, onCancel }: Props) {
  const [title, setTitle]             = useState(initialTitle ?? '');
  const [collections, setCollections] = useState<Collection[]>([]);
  const [colId, setColId]             = useState<string>(collectionId ?? '');
  const [assignee, setAssignee]       = useState<Person | null>(null);
  const [startDate, setStartDate]     = useState('');
  const [dueDate, setDueDate]         = useState('');
  const [status, setStatus]           = useState<number>(TaskStatus.NotStarted);
  const [priority, setPriority]       = useState<PriorityChoice>('none');
  const [taskType, setTaskType]       = useState<number>(initialType ?? TaskType.Task);
  const [sprints, setSprints]         = useState<Sprint[]>([]);
  const [sprintId, setSprintId]       = useState<string>(initialSprintId ?? '');
  const [description, setDescription] = useState(initialDescription ?? '');
  const [checklist, setChecklist]     = useState<DraftChecklistItem[]>([]);
  const [stagedTags, setStagedTags]   = useState<Tag[]>([]);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const [newChecklistItem, setNewChecklistItem] = useState('');
  const [saving, setSaving]           = useState(false);
  const [titleError, setTitleError]   = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [recurring, setRecurring]     = useState(false);
  const [recurrence, setRecurrence]   = useState<RecurrenceInput>(DEFAULT_RECURRENCE);

  useEffect(() => {
    getProjectCollections(pca, projectId).then(cs => {
      setCollections(cs);
      if (!colId && cs.length > 0) setColId(cs[0].dagrun_pm_collectionid);
    }).catch(() => { /* best effort */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pca, projectId]);

  // Agile projects: offer a Sprint assignment on the new item. Only open
  // (Planned / Active) sprints are selectable — you don't add work to a
  // Completed or Archived sprint.
  useEffect(() => {
    if (!agile) return;
    getProjectSprints(pca, projectId)
      .then(ss => setSprints(ss.filter(s => s.dagrun_pm_status === SprintStatus.Planned || s.dagrun_pm_status === SprintStatus.Active)))
      .catch(() => { /* best effort — Sprint field just stays empty */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pca, projectId, agile]);

  const addChecklistItem = () => {
    const text = newChecklistItem.trim();
    if (!text) return;
    setChecklist(arr => [...arr, { tempId: `tmp${nextTempId++}`, text }]);
    setNewChecklistItem('');
  };
  const removeChecklistItem = (tempId: string) => {
    setChecklist(arr => arr.filter(x => x.tempId !== tempId));
  };

  const handleSave = async () => {
    if (!title.trim()) { setTitleError(true); return; }
    setSaving(true);
    setError(null);
    try {
      const id = await createTask(pca, projectId, {
        title: title.trim(),
        collectionId: colId || undefined,
        priority: PRIORITY_VALUE[priority] ?? undefined,
        status,
        taskType,
        assigneeId: assignee?.aadObjectId ?? null,
        assigneeName: assignee?.displayName ?? null,
        dueDate: dueDate || null,
        startDate: startDate || null,
        description: description.trim() || null,
        sortOrder: initialSortOrder ?? 999999,
      });
      // POST checklist items after the task exists. Sequential so sortorder is monotonic.
      for (let i = 0; i < checklist.length; i++) {
        try { await createChecklistItem(pca, id, checklist[i].text, i + 1); }
        catch { /* best effort — don't block task create on checklist failure */ }
      }
      // Commit staged tags — junction records were deferred until the task
      // had an ID. If createTask threw above, we never get here and the
      // staged selection is simply discarded (no junctions were written).
      await attachTags(pca, TASK_TAG_POOL, id, title.trim(), stagedTags);
      // Agile: drop the new item into the chosen sprint (best-effort — a
      // failure here still leaves a valid backlog item).
      if (agile && sprintId) {
        try { await moveTaskToSprint(pca, id, sprintId); } catch { /* leave in backlog */ }
      }
      // Recurrence: the just-created task is the series template. Instance
      // generation is best-effort — a failure here still leaves a valid
      // task, so surface it without discarding the create.
      if (recurring && (recurrence.end.type === 'count' || recurrence.end.date)) {
        const template = {
          dagrun_pm_taskid: id,
          dagrun_pm_name: title.trim(),
          dagrun_pm_status: status,
          dagrun_pm_percentcomplete: 0,
          dagrun_pm_duedate: dueDate || null,
          dagrun_pm_startdate: startDate || null,
          dagrun_pm_priority: PRIORITY_VALUE[priority],
          dagrun_pm_assigneeid: assignee?.aadObjectId ?? null,
          dagrun_pm_assigneename: assignee?.displayName ?? null,
          dagrun_pm_description: description.trim() || null,
          _dagrun_pm_projectid_value: projectId,
          _dagrun_pm_wipcollectionid_value: colId || null,
        } as unknown as Task;
        try {
          await createRecurrence(pca, template, recurrence);
        } catch (e) {
          console.error('Recurrence generation failed', e);
        }
      }
      onCreated(id);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Create failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onCancel}
      style={{ background: 'rgba(15,17,23,0.18)' }}
    >
      <div
        className="modal-panel"
        onClick={e => e.stopPropagation()}
        style={{
          width: 520, maxWidth: '95vw',
          maxHeight: '90vh', overflowY: 'auto',
          borderRadius: 'var(--r-xl)',
          boxShadow: 'var(--shadow-panel)',
          padding: 24,
        }}
      >
        <h3 style={{ margin: '0 0 16px 0', fontSize: 18 }}>New Task</h3>

        {/* Title — prominent, first and dominant */}
        <div style={{ marginBottom: 18 }}>
          <label style={fieldLabel}>Task Title *</label>
          <input
            autoFocus
            value={title}
            onChange={e => { setTitle(e.target.value); if (titleError) setTitleError(false); }}
            onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}
            placeholder="e.g. Draft Q2 kickoff deck"
            style={titleError ? { ...BM_TITLE_INPUT, borderColor: 'var(--danger)' } : BM_TITLE_INPUT}
          />
          {titleError && (
            <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>Title is required.</div>
          )}
        </div>

        {/* Basic Info */}
        <div style={firstSectionLabel}>Basic Info</div>

        {/* Type — Task vs Story segmented control. Subtask is not offered
            here; subtasks are created from a parent task's Subtasks tab.
            Both types are always available (Story is not gated on the Agile
            module — Agile projects simply default new backlog items to Story
            and add the Sprint field below). */}
        <div style={{ marginBottom: 14 }}>
          <label style={fieldLabel}>Type</label>
          <div style={{ display: 'flex', gap: 6 }}>
            {[{ v: TaskType.Task, label: 'Task' }, { v: TaskType.Story, label: 'Story' }].map(o => {
              const active = taskType === o.v;
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => setTaskType(o.v)}
                  style={{
                    flex: 1,
                    padding: '7px 12px',
                    background: active ? 'var(--brand-mist)' : 'var(--surface)',
                    color:      active ? 'var(--brand)'      : 'var(--ink-3)',
                    border: `1px solid ${active ? 'var(--brand)' : 'var(--border)'}`,
                    borderRadius: 'var(--r-sm)',
                    fontFamily: 'var(--font-ui)',
                    fontSize: 13,
                    fontWeight: active ? 500 : 400,
                    cursor: 'pointer',
                    transition: 'all 0.12s',
                  }}
                >{o.label}</button>
              );
            })}
          </div>
        </div>

        {/* Sprint assignment — Agile projects only. Defaults to Backlog. */}
        {agile && (
          <div style={{ marginBottom: 14 }}>
            <label style={fieldLabel}>Sprint</label>
            <select value={sprintId} onChange={e => setSprintId(e.target.value)}>
              <option value="">Backlog (no sprint)</option>
              {sprints.map(s => (
                <option key={s.dagrun_pm_sprintid} value={s.dagrun_pm_sprintid}>{s.dagrun_pm_name}</option>
              ))}
            </select>
          </div>
        )}

        <div style={{ marginBottom: 14 }}>
          <label style={fieldLabel}>Collection</label>
          <select value={colId} onChange={e => setColId(e.target.value)}>
            {collections.length === 0 && <option value="">— No collections —</option>}
            {collections.map(c => (
              <option key={c.dagrun_pm_collectionid} value={c.dagrun_pm_collectionid}>
                {c.dagrun_pm_name}
              </option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={fieldLabel}>Assignee</label>
          <PeoplePicker pca={pca} value={assignee} onChange={setAssignee} placeholder="Assign to…" />
        </div>

        {/* Schedule */}
        <div style={sectionLabel}>Schedule</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={fieldLabel}>Start Date</label>
            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
          </div>
          <div>
            <label style={fieldLabel}>Due Date</label>
            <input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} />
          </div>
        </div>

        {/* Details */}
        <div style={sectionLabel}>Details</div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
          <div>
            <label style={fieldLabel}>Status</label>
            <select value={status} onChange={e => setStatus(Number(e.target.value))}>
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label style={fieldLabel}>Priority</label>
            <select value={priority} onChange={e => setPriority(e.target.value as PriorityChoice)}>
              <option value="none">None</option>
              <option value="low">Low</option>
              <option value="medium">Medium</option>
              <option value="high">High</option>
              <option value="critical">Critical</option>
            </select>
          </div>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={fieldLabel}>Tags</label>
          <TagSection
            pca={pca}
            pool={TASK_TAG_POOL}
            projectId={projectId}
            stagedTags={stagedTags}
            onStagedChange={setStagedTags}
          />
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={fieldLabel}>Description / Note</label>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            placeholder="Optional note…"
            rows={3}
            style={{ minHeight: 70 }}
          />
        </div>

        {/* Checklist — collapsible */}
        <div style={{ marginBottom: 14 }}>
          <div
            onClick={() => setChecklistOpen(o => !o)}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              cursor: 'pointer', userSelect: 'none',
              ...fieldLabel,
              marginBottom: checklistOpen ? 8 : 0,
            }}
          >
            <span style={{ fontSize: 10, color: 'var(--ink-4)' }}>{checklistOpen ? '▾' : '▸'}</span>
            <span>Checklist{checklist.length > 0 ? ` (${checklist.length})` : ''}</span>
          </div>
          {checklistOpen && (
            <div style={{
              border: '1px solid var(--border)',
              borderRadius: 6, padding: 10,
              background: 'var(--surface-2)',
            }}>
              {checklist.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--ink-4)', marginBottom: 8, fontStyle: 'italic' }}>
                  No items yet.
                </div>
              )}
              {checklist.map(item => (
                <div key={item.tempId} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <input type="checkbox" disabled checked={false} style={{ width: 14, height: 14, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.text}</span>
                  <button type="button" onClick={() => removeChecklistItem(item.tempId)}
                    style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--ink-4)', fontSize: 13, padding: 2 }}
                    aria-label="Remove"
                  >×</button>
                </div>
              ))}
              <div style={{ display: 'flex', gap: 6, marginTop: checklist.length > 0 ? 6 : 0 }}>
                <input
                  type="text"
                  value={newChecklistItem}
                  onChange={e => setNewChecklistItem(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addChecklistItem(); } }}
                  placeholder="+ Add item and press Enter"
                  style={{ flex: 1, fontSize: 13 }}
                />
                <button type="button" onClick={addChecklistItem} disabled={!newChecklistItem.trim()}
                  style={{
                    background: 'var(--brand)', color: '#fff', border: 'none',
                    borderRadius: 4, padding: '4px 10px',
                    fontFamily: 'var(--font-ui)', fontSize: 12, fontWeight: 600,
                    cursor: newChecklistItem.trim() ? 'pointer' : 'not-allowed',
                    opacity: newChecklistItem.trim() ? 1 : 0.5,
                  }}
                >+</button>
              </div>
            </div>
          )}
        </div>

        {/* Recurrence */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--ink-2)', cursor: 'pointer', fontFamily: 'var(--font-ui)', marginBottom: recurring ? 8 : 0 }}>
            <input type="checkbox" checked={recurring} onChange={e => setRecurring(e.target.checked)} />
            Make Recurring
          </label>
          {recurring && (
            <>
              <RecurrenceControls value={recurrence} onChange={setRecurrence} />
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 6 }}>
                Instances are generated from the due date (or start date) when the task is created.
              </div>
            </>
          )}
        </div>

        {error && (
          <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--danger)' }}>{error}</div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button className="btn-secondary" onClick={onCancel} disabled={saving}>Cancel</button>
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Creating…' : 'Create Task'}
          </button>
        </div>
      </div>
    </div>
  );
}
